<template>
  <v-card
    elevation="0"
    light
    style="width: 210mm; min-height: 296mm; padding: 10mm"
  >
    <div class="d-flex gap-3">
      <div
        :style="{
          backgroundImage: `url('/img/logo/logo-dark.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: 'center',
          backgroundPositionY: 'start',
          width: '100px',
          height: '50px',
        }"
      ></div>
      <div class="flex-grow-1">
        <h4 class="mb-0 lh-5">Pagamento #{{ payment?.id | shortId }}</h4>
        <p class="mb-0 text-caption font-weight-bold">{{ payment?.id }}</p>
      </div>
      <div class="text-right">
        <h3 class="mb-0 lh-5">{{ payment.amount | currency }}</h3>
        <p class="mb-0">
          {{ PAYMENT.paymentMethod[payment.paymentMethod]?.text }} •
          {{ PAYMENT.paymentType[payment.paymentType]?.text }}
        </p>
      </div>
    </div>

    <!-- Evento -->
    <v-card v-if="payment.Ticket" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Evento</span>
      <h5 class="mb-0 lh-3">{{ party?.name }}</h5>
      <span class="text-caption">{{
        party?.date | date("DD/MM/YYYY HH:mm")
      }}</span>
      <v-divider class="my-2"></v-divider>

      <p class="font-weight-bold mb-0">Ingressos</p>
      <v-list dense>
        <v-list-item
          v-for="ticketBlock in Object.values(reducedTickets)"
          :key="ticketBlock.id"
        >
          <v-list-item-avatar>
            <h4 class="mb-0">{{ ticketBlock.tickets.length }}x</h4>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ ticketBlock.TicketGroup.name }} •
              {{ ticketBlock.name }}
            </v-list-item-title>
            <v-list-item-subtitle
              class="d-flex flex-wrap gap-x-1 gap-y-1 align-center"
            >
              ID{{ ticketBlock.tickets.length > 1 ? "s" : "" }}:
              <v-chip
                v-for="(ticket, i) in ticketBlock.tickets"
                :key="ticket.id"
                x-small
                label
              >
                #{{ ticket.id | shortId }}
              </v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Comprador -->
    <v-card v-if="payment.Payer" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Comprador</span>
      <h6 class="mb-0">{{ payment.Payer.name }}</h6>
      <div class="d-flex gap-6">
        <div>
          <div class="">
            <p class="mb-0 lh-1">Telefone</p>
            <p class="mb-0 font-weight-bold">
              {{ payment.Payer.phone }}
            </p>
          </div>
        </div>
        <div>
          <div class="">
            <p class="mb-0 lh-1">Email</p>
            <p class="mb-0 font-weight-bold">
              {{ payment.Payer.email }}
            </p>
          </div>
        </div>
        <div>
          <div class="">
            <p class="mb-0 lh-1">Documento</p>
            <p class="mb-0 font-weight-bold">
              {{ obfuscatedCPF }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="payment.Payer.ips.length > 0" class="mt-1">
        <div class="">
          <p class="mb-0 lh-1">Últimos IPs acessos</p>
          <p class="mb-0 font-weight-bold">
            {{ payment.Payer.ips.join(", ") }}
          </p>
        </div>
      </div>
    </v-card>

    <!-- Detalhes do pagamento -->
    <v-card outlined class="mt-3 pa-3 rounded-lg">
      <span class="font-weight-bold mb-0">Detalhes do pagamento</span>
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Parametro</th>
              <th class="text-left">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ID</td>
              <td>{{ payment.id }}</td>
            </tr>
            <tr v-if="payment.integrationId">
              <td>ID da integração</td>
              <td>{{ payment.integrationId }}</td>
            </tr>
            <tr>
              <td>Data de criação</td>
              <td>{{ payment.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
            </tr>
            <tr v-if="payment.validity">
              <td>Vencimento</td>
              <td>{{ payment.validity | date("DD/MM/YYYY HH:mm") }}</td>
            </tr>
            <tr v-if="payment.payedAt">
              <td>Data de pagamento</td>
              <td>{{ payment.payedAt | date("DD/MM/YYYY HH:mm") }}</td>
            </tr>
            <tr v-if="payment.refundedAt">
              <td>Data de reembolso</td>
              <td>{{ payment.refundedAt | date("DD/MM/YYYY HH:mm") }}</td>
            </tr>
            <tr>
              <td>Meio de pagamento</td>
              <td>{{ PAYMENT.paymentMethod[payment.paymentMethod]?.text }}</td>
            </tr>
            <tr>
              <td>Forma de pagamento</td>
              <td>{{ PAYMENT.paymentType[payment.paymentType]?.text }}</td>
            </tr>
            <tr
              v-if="
                payment.installments > 1 ||
                payment.paymentType === 'CREDIT_CARD'
              "
            >
              <td>Parcelas</td>
              <td>{{ payment.installments }}x</td>
            </tr>
            <tr v-if="!payment.chargeback">
              <td>Problema</td>
              <td class="error--text">Contestado</td>
            </tr>
            <tr v-if="seller">
              <td>{{ seller.type }}</td>
              <td>{{ seller.name }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-divider class="my-2"></v-divider>

      <p class="font-weight-bold mb-0">Valores</p>
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Item</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Valor do carrinho</td>
              <td class="text-right">
                {{ (payment.amount - payment.clientFee) | currency }}
              </td>
            </tr>
            <tr>
              <td>Taxa de Serviço</td>
              <td class="text-right">
                {{ payment.clientFee | currency }}
              </td>
            </tr>
            <tr v-if="payment.installmentsFee">
              <td>Taxa de Parcelamento</td>
              <td class="text-right">
                {{ payment.clientinstallmentsFeeFee | currency }}
              </td>
            </tr>
            <tr>
              <td>Valor Pago</td>
              <td class="font-weight-bold text-right">
                {{ (payment.amount + payment.installmentsFee) | currency }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>

    <v-card
      outlined
      class="mt-3 pa-3 rounded-lg"
    >
      <span class="font-weight-bold"> Solicitações de reembolso </span>
      <v-alert
        v-if="payment.RefoundRequest?.length === 0"
        type="info"
        class="mb-0"
        text
        dense
      >
        Nenhuma solicitação de reembolso recebida
      </v-alert>
      <v-simple-table v-else dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Data de criação</th>
              <th class="text-left">Data de resposta</th>
              <th class="text-left">Motivo</th>
              <th class="text-left">Resposta</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="refund in payment.RefoundRequest" :key="refund.id">
              <td>{{ refund.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
              <td v-if="refund.closedAt">
                {{ refund.closedAt | date("DD/MM/YYYY HH:mm") }}
              </td>
              <td v-else><i>Em aberto</i></td>
              <td>{{ refund.reason }}</td>
              <td>{{ refund.answer }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </v-card>
</template>

<script>
import PAYMENT from "@/utils/payment";

export default {
  data: () => ({
    PAYMENT,
  }),
  props: {
    payment: {
      type: Object,
      default: () => null,
    },
    party: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    obfuscatedCPF() {
      if (!this.payment?.Payer?.document) return "";
      return this.payment.Payer.document
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.***.***-$4");
    },
    seller() {
      if (this.payment.PosSession)
        return {
          type: this.payment.PosSession.partyId ? "Bilheteria" : "PDV",
          name:
            this.payment.PosSession?.name ||
            this.payment.PosSession?.Address?.name,
        };

      const [ticket] = this.payment?.Ticket || [];
      if (ticket?.Seller) return { type: "Vendedor", name: ticket.Seller.name };

      return { type: "Online", name: "Online" };
    },
    saleLink() {
      const ticket = this.payment?.Ticket[0];
      return ticket.SaleLink;
    },
    reducedTickets() {
      const tickets = this.payment?.Ticket || [];
      return tickets.reduce((acc, ticket) => {
        const key = ticket.TicketBlock.id;
        if (!acc[key])
          acc[key] = {
            ...ticket.TicketBlock,
            tickets: [],
          };
        acc[key].tickets.push(ticket);
        return acc;
      }, {});
    },
  },
};
</script>
